import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Clancy Tuning';
const SITE_URL = 'https://clancytuning.com';
const DEFAULT_DESCRIPTION =
  'AI-assisted ECU tuning, engineered for safety. Free desktop app for Windows and macOS plus a community of tuners and a datalog analyzer.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/clancy-tuning-logo.png`;

interface SeoProps {
  title: string;
  description?: string;
  image?: string;
  /** Mark this page as not-for-indexing (e.g. placeholders). */
  noindex?: boolean;
}

/** Lightweight per-page meta updater — no react-helmet dependency. */
export function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  noindex = false,
}: SeoProps) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} · ${SITE_NAME}`;
    document.title = fullTitle;

    const url = `${SITE_URL}${pathname === '/' ? '' : pathname}`;

    setMeta('name', 'description', description);
    setMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');

    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', image);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', SITE_NAME);

    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);

    setLink('canonical', url);
  }, [title, description, image, noindex, pathname]);

  return null;
}

function setMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
